import { useMemo } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import ModalShell from "../modal/ModalShell";
import AppStoreBlocks from "./AppStoreBlocks";
import { detectPlatform, showsQrCodes, storesFor } from "../../features/app-stores/platform";

const TITLE_ID = "get-the-app-title";

interface GetTheAppDialogProps {
  onClose: () => void;
  /** Who reads it: the instructor's line names their members, the member's does not. */
  audience?: "instructor" | "member";
}

/**
 * The Timeplanned app's store links, shaped like the share dialog. A phone sees only its own store
 * and no QR code; a computer sees both stores, each with a QR code to scan from the phone.
 * It mounts on document.body: its openers (the sticky left menu, the transformed drawer, the portal
 * header) each make their own stacking context, which would leave the header and later cards above it.
 */
const GetTheAppDialog = ({ onClose, audience = "instructor" }: GetTheAppDialogProps) => {
  const { t } = useTranslation();
  const platform = useMemo(() => detectPlatform(), []);

  return createPortal(
    <ModalShell onClose={onClose} labelledBy={TITLE_ID}>
      <div className="p-4 lg:p-6" data-testid="get-the-app-dialog">
        <h2 id={TITLE_ID} className="text-lg font-semibold text-grey-20 dark:text-white">
          {t("getTheApp.title")}
        </h2>
        <p className="text-sm text-grey-40 dark:text-grey-80">{t(audience === "member" ? "getTheApp.dialogHintMember" : "getTheApp.dialogHint")}</p>
        <div className="mt-2">
          <AppStoreBlocks stores={storesFor(platform)} showQr={showsQrCodes(platform)} />
        </div>
        <div className="mt-2 flex justify-end">
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            {t("common.done")}
          </button>
        </div>
      </div>
    </ModalShell>,
    document.body,
  );
};

export default GetTheAppDialog;
